import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Card, Checkbox, Col, DatePicker, Descriptions, Form, Input, InputNumber, Row, Select, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, {
  ADDITION_TREATMENTS,
  ADJUSTMENT_ALLOWED_STATUSES,
  DAY_COUNT_METHODS,
  PARTIAL_MONTH_POLICIES,
  RECOGNITION_METHODS,
  REVISION_TYPES,
  SUSPENSION_TREATMENTS,
  type AdjustmentInput,
  type RevisionType,
} from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useProjectsForSelection } from '@/hooks/useProject';
import { DAY_COUNT_LABEL, METHOD_LABEL, StatusTag, accountLabel, amount, apiErrorMessage, contractPath, humanize, options, useRecognitionAccess } from './prepaidDisplay';
import { AllocationEditor, CustomScheduleEditor, allocationProblem, type AllocationRow, type CustomLineRow } from './PrepaidFormParts';

const { Title, Text, Paragraph } = Typography;

const HELP: Record<RevisionType, string> = {
  REVISION: 'Changes the end date, method or day count from the effective date. Posted lines stay; future lines are regenerated from the remaining balance.',
  EXTENSION: 'Moves the service end date later and spreads the remaining balance over the longer period.',
  ADDITION: 'Adds an amount to the prepayment, either spread over the remaining period or as a separate child schedule.',
  CHANGE_GL: 'Changes the expense account for recognition from the effective date. Posted recognition is not reclassified.',
  CHANGE_DIMENSIONS: 'Changes cost centre, project, department and/or allocation for recognition from the effective date.',
  SUSPENSION: 'Pauses recognition. Choose whether the paused days extend the service end, are redistributed, or are held until resume.',
  RESUME: 'Resumes recognition after a suspension and regenerates the remaining schedule.',
  TERMINATION: 'Ends the contract early. The remaining balance is split into refund receivable, write-off and termination expense.',
  REFUND: 'Records a supplier refund that reduces the remaining prepaid balance (Dr refund receivable / Cr prepaid asset).',
  WRITE_OFF: 'Writes off part of the remaining prepaid balance (Dr write-off expense / Cr prepaid asset).',
  REVERSAL: 'Reverses a posted recognition line with a reversing journal; optionally schedules it to be recognised again.',
};

const d = (v: dayjs.Dayjs | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD') : null);

const PrepaidAdjustmentPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [form] = Form.useForm();
  const type = (params.get('type') || '').toUpperCase() as RevisionType;
  const lineId = params.get('line');
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [customLines, setCustomLines] = useState<CustomLineRow[]>([]);
  const [additionBase, setAdditionBase] = useState<string | null>(null);

  const method = Form.useWatch('recognition_method', form);
  const additionTreatment = Form.useWatch('addition_treatment', form);
  const postJournal = Form.useWatch('post_journal', form);
  const suspensionTreatment = Form.useWatch('suspension_treatment', form);
  const additionAmount = Form.useWatch('amount', form);
  const additionRate = Form.useWatch('exchange_rate', form);
  const refundAmount = Form.useWatch('refund_amount', form);
  const writeOffAmount = Form.useWatch('write_off_amount', form);

  const { data: c, isLoading } = useQuery({ queryKey: ['recognition', 'contract', id], queryFn: () => recognitionService.getContract(id!), enabled: Boolean(id) });
  const { data: policy } = useQuery({ queryKey: ['recognition', 'policy'], queryFn: () => recognitionService.getPolicy() });
  const { data: postedLines } = useQuery({
    queryKey: ['recognition', 'contract', id, 'schedule', 'posted'],
    queryFn: () => recognitionService.contractSchedule(id!, { status: 'POSTED,PARTIALLY_POSTED', limit: 500 }),
    enabled: Boolean(id) && type === 'REVERSAL',
  });
  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: projects = [] } = useProjectsForSelection();

  const expenseOptions = useMemo(() => expenseAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })), [expenseAccounts]);
  const balanceOptions = useMemo(
    () => [...assetAccounts, ...liabilityAccounts].map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })),
    [assetAccounts, liabilityAccounts]
  );
  const projectOptions = useMemo(
    () => (projects as any[]).map((p) => ({ value: p.id, label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name ?? p.id })),
    [projects]
  );
  const departmentOptions = useMemo(() => departments.map((x) => ({ value: x.id, label: `${x.department_code} - ${x.department_name}` })), [departments]);

  useEffect(() => {
    if (!c) return;
    form.setFieldsValue({
      effective_date: dayjs(),
      recognition_method: c.recognition_method,
      day_count_method: c.day_count_method,
      partial_month_policy: c.partial_month_policy,
      expense_account_id: type === 'CHANGE_GL' ? undefined : c.expense_account_id,
      cost_center: c.cost_center,
      project_id: c.project_id ?? undefined,
      department_id: c.department_id ?? undefined,
      addition_treatment: 'SPREAD_OVER_REMAINING_PERIOD',
      suspension_treatment: 'HOLD_RECOGNITION',
      exchange_rate: Number(c.exchange_rate),
      schedule_line_id: lineId ?? undefined,
      re_recognize: false,
    });
    if (type === 'CHANGE_DIMENSIONS') {
      setAllocations(
        c.allocations.map((a) => ({
          key: a.id,
          allocation_basis: a.allocation_basis,
          percentage: a.percentage,
          fixed_amount: a.fixed_amount,
          expense_account_id: a.expense_account_id,
          cost_center: a.cost_center,
          project_id: a.project_id,
          department_id: a.department_id,
          branch_id: a.branch_id,
          effective_from: a.effective_from,
          effective_to: a.effective_to,
        }))
      );
    }
  }, [c, form, type, lineId]);

  useEffect(() => {
    if (type !== 'ADDITION' || !c || !additionAmount || Number(additionAmount) <= 0) {
      setAdditionBase(null);
      return;
    }
    const t = window.setTimeout(() => {
      recognitionService
        .previewAddition(c.id, additionAmount, additionRate)
        .then((r) => setAdditionBase(r.base_amount))
        .catch(() => setAdditionBase(null));
    }, 400);
    return () => window.clearTimeout(t);
  }, [type, c, additionAmount, additionRate]);

  const submit = useMutation({
    mutationFn: (input: AdjustmentInput) => recognitionService.createAdjustment(id!, input),
    onSuccess: (r) => {
      message.success(r.status === 'APPLIED' ? 'Adjustment applied' : 'Adjustment submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['recognition'] });
      navigate(`${contractPath(id!)}?tab=adjustments`);
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Adjustment failed')),
  });

  if (!REVISION_TYPES.includes(type)) return <Alert type="error" showIcon style={{ margin: 24 }} message={t('unknownAdjustmentType')} />;
  if (!access.canAdjust(type)) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('noPermissionForAdjustments', { type: humanize(type).toLowerCase() })} />;
  if (isLoading || !c) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!ADJUSTMENT_ALLOWED_STATUSES[type].includes(c.status)) {
    return (
      <Alert
        style={{ margin: 24 }}
        type="info"
        showIcon
        message={`${humanize(type)} is not available while ${c.recognition_number} is ${humanize(c.status).toLowerCase()}.`}
        action={<Button onClick={() => navigate(contractPath(c.id))}>{t('openContract')}</Button>}
      />
    );
  }

  const ccy = c.transaction_currency;
  const foreign = c.transaction_currency !== c.base_currency;
  const remainingCents = Math.round(Number(c.remaining_amount) * 100);
  const terminationExpense =
    type === 'TERMINATION' ? (remainingCents - Math.round(Number(refundAmount || 0) * 100) - Math.round(Number(writeOffAmount || 0) * 100)) / 100 : 0;

  const onSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const input: AdjustmentInput = { revision_type: type, effective_date: d(values.effective_date)!, reason: values.reason.trim() };
    switch (type) {
      case 'REVISION':
        Object.assign(input, {
          service_end_date: d(values.service_end_date),
          recognition_end_date: d(values.recognition_end_date),
          recognition_method: values.recognition_method !== c.recognition_method ? values.recognition_method : null,
          day_count_method: values.day_count_method !== c.day_count_method ? values.day_count_method : null,
          partial_month_policy: values.partial_month_policy !== c.partial_month_policy ? values.partial_month_policy : null,
          custom_lines: values.recognition_method === 'CUSTOM_SCHEDULE' ? customLines.map(({ key: _k, ...l }) => l) : undefined,
        });
        break;
      case 'EXTENSION':
        Object.assign(input, { service_end_date: d(values.service_end_date), recognition_end_date: d(values.recognition_end_date) });
        break;
      case 'ADDITION':
        Object.assign(input, {
          amount: values.amount,
          exchange_rate: foreign ? values.exchange_rate : null,
          addition_treatment: values.addition_treatment,
          post_journal: Boolean(values.post_journal),
          source_clearing_account_id: values.post_journal ? values.source_clearing_account_id : null,
          source_document_number: values.source_document_number || null,
          separate_service_start: values.addition_treatment === 'SEPARATE_SCHEDULE' ? d(values.separate_service_start) : null,
          separate_service_end: values.addition_treatment === 'SEPARATE_SCHEDULE' ? d(values.separate_service_end) : null,
        });
        break;
      case 'CHANGE_GL':
        input.expense_account_id = values.expense_account_id;
        break;
      case 'CHANGE_DIMENSIONS': {
        const problem = allocationProblem(allocations, c.remaining_amount, t);
        if (problem) {
          message.error(problem);
          return;
        }
        Object.assign(input, {
          cost_center: values.cost_center ?? null,
          project_id: values.project_id ?? null,
          department_id: values.department_id ?? null,
          allocations: allocations.map(({ key: _k, ...a }) => a),
        });
        break;
      }
      case 'SUSPENSION':
        Object.assign(input, { suspension_treatment: values.suspension_treatment, suspension_end_date: d(values.suspension_end_date) });
        break;
      case 'TERMINATION':
        if (terminationExpense < 0) {
          message.error(t('refundPlusWriteOffExceeds'));
          return;
        }
        Object.assign(input, {
          refund_amount: values.refund_amount || 0,
          write_off_amount: values.write_off_amount || 0,
          refund_account_id: values.refund_account_id ?? null,
          write_off_account_id: values.write_off_account_id ?? null,
          source_document_number: values.source_document_number || null,
        });
        break;
      case 'REFUND':
      case 'WRITE_OFF':
        Object.assign(input, { amount: values.amount, account_id: values.account_id ?? null, source_document_number: values.source_document_number || null });
        break;
      case 'REVERSAL':
        Object.assign(input, { schedule_line_id: values.schedule_line_id, re_recognize: Boolean(values.re_recognize) });
        break;
      default:
        break;
    }
    submit.mutate(input);
  };

  const needsApproval = (() => {
    const events = new Set(policy?.approval_required_events ?? []);
    const map: Record<RevisionType, string> = {
      REVISION: 'REVISE',
      EXTENSION: 'CHANGE_SERVICE_DATES',
      ADDITION: 'REVISE',
      CHANGE_GL: 'CHANGE_GL',
      CHANGE_DIMENSIONS: 'REVISE',
      SUSPENSION: 'SUSPEND',
      RESUME: 'RESUME',
      TERMINATION: 'TERMINATE',
      REFUND: 'REFUND',
      WRITE_OFF: 'WRITE_OFF',
      REVERSAL: 'REVERSE',
    };
    return events.has(map[type]);
  })();

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(contractPath(c.id))}>
          {tc('back')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {humanize(type)} — {c.recognition_number}
        </Title>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label={t('description')}>{c.description}</Descriptions.Item>
          <Descriptions.Item label={tc('status')}>
            <StatusTag status={c.status} />
          </Descriptions.Item>
          <Descriptions.Item label={t('service')}>
            {c.service_start_date} → {c.service_end_date}
          </Descriptions.Item>
          <Descriptions.Item label={t('recognisable')}>{amount(c.recognizable_amount, ccy)}</Descriptions.Item>
          <Descriptions.Item label={t('recognised')}>{amount(c.recognized_amount, ccy)}</Descriptions.Item>
          <Descriptions.Item label={t('remaining')}>{amount(c.remaining_amount, ccy)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={HELP[type]} description={needsApproval ? 'Company policy requires approval: the adjustment applies once approved.' : undefined} />

      <Form form={form} layout="vertical">
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="effective_date" label={t('effectiveDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item name="reason" label={t('reason')} rules={[{ required: true, whitespace: true, min: 3, max: 1000 }]}>
                <Input placeholder={t('whyAdjustmentNeeded')} />
              </Form.Item>
            </Col>

            {(type === 'REVISION' || type === 'EXTENSION') && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="service_end_date" label={t('newServiceEnd')} rules={type === 'EXTENSION' ? [{ required: true }] : []} extra={`Currently ${c.service_end_date}`}>
                    <DatePicker style={{ width: '100%' }} disabledDate={type === 'EXTENSION' ? (x) => !x.isAfter(dayjs(c.service_end_date), 'day') : undefined} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item name="recognition_end_date" label={t('newRecognitionEnd')} extra={`Currently ${c.recognition_end_date}; blank follows service end`}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}
            {type === 'REVISION' && (
              <>
                <Col xs={12} md={4}>
                  <Form.Item name="recognition_method" label={t('method')}>
                    <Select options={options(RECOGNITION_METHODS, METHOD_LABEL)} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="day_count_method" label={t('dayCount')}>
                    <Select options={options(DAY_COUNT_METHODS, DAY_COUNT_LABEL)} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="partial_month_policy" label={t('partialMonth')}>
                    <Select options={options(PARTIAL_MONTH_POLICIES)} />
                  </Form.Item>
                </Col>
                {method === 'CUSTOM_SCHEDULE' && (
                  <Col span={24}>
                    <Card size="small" type="inner" title={t('customScheduleRemaining')}>
                      <CustomScheduleEditor rows={customLines} onChange={setCustomLines} target={c.remaining_amount} />
                    </Card>
                  </Col>
                )}
              </>
            )}

            {type === 'ADDITION' && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="amount" label={`Addition amount (${ccy})`} rules={[{ required: true }]} extra={additionBase && foreign ? `≈ ${amount(additionBase, c.base_currency)}` : undefined}>
                    <InputNumber min={0.01} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                {foreign && (
                  <Col xs={12} md={6}>
                    <Form.Item name="exchange_rate" label={`Rate to ${c.base_currency}`} rules={[{ required: true }]}>
                      <InputNumber min={0.000001} precision={6} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} md={6}>
                  <Form.Item name="addition_treatment" label={t('treatment')}>
                    <Select options={options(ADDITION_TREATMENTS)} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="source_document_number" label={t('sourceDocumentNo')}>
                    <Input placeholder={t('exampleSupplementaryInvoice')} />
                  </Form.Item>
                </Col>
                {additionTreatment === 'SEPARATE_SCHEDULE' && (
                  <>
                    <Col xs={12} md={6}>
                      <Form.Item name="separate_service_start" label={t('separateServiceStart')} extra="Blank = effective date">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name="separate_service_end" label={t('separateServiceEnd')} extra={`Blank = ${c.service_end_date}`}>
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </>
                )}
                <Col xs={24} md={6}>
                  <Form.Item name="post_journal" valuePropName="checked" label={t('capitalisationJournal')}>
                    <Checkbox>{t('postDrPrepaidCrClearingNow')}</Checkbox>
                  </Form.Item>
                </Col>
                {postJournal && (
                  <Col xs={24} md={8}>
                    <Form.Item name="source_clearing_account_id" label={t('clearingAccountCredit')} rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="label" options={balanceOptions} />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}

            {type === 'CHANGE_GL' && (
              <Col xs={24} md={10}>
                <Form.Item name="expense_account_id" label={t('newExpenseAccount')} rules={[{ required: true }]} extra={`Currently ${accountLabel(c.expense_account_code, c.expense_account_name)}`}>
                  <Select showSearch optionFilterProp="label" options={expenseOptions.filter((o) => o.value !== c.expense_account_id)} />
                </Form.Item>
              </Col>
            )}

            {type === 'CHANGE_DIMENSIONS' && (
              <>
                <Col xs={24} md={6}>
                  <Form.Item name="cost_center" label={t('costCentre')}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item name="project_id" label={t('project')}>
                    <Select allowClear showSearch optionFilterProp="label" options={projectOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item name="department_id" label={t('department')}>
                    <Select allowClear showSearch optionFilterProp="label" options={departmentOptions} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Card size="small" type="inner" title={t('allocationReplacesFromDate')}>
                    <AllocationEditor
                      rows={allocations}
                      onChange={setAllocations}
                      expenseOptions={expenseOptions}
                      projectOptions={projectOptions}
                      departmentOptions={departmentOptions}
                      recognizable={c.remaining_amount}
                    />
                  </Card>
                </Col>
              </>
            )}

            {type === 'SUSPENSION' && (
              <>
                <Col xs={24} md={8}>
                  <Form.Item name="suspension_treatment" label={t('treatmentOfPausedDays')}>
                    <Select options={options(SUSPENSION_TREATMENTS)} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    name="suspension_end_date"
                    label={t('suspensionEnd')}
                    rules={suspensionTreatment !== 'HOLD_RECOGNITION' ? [{ required: true, message: t('requiredForTreatment') }] : []}
                    extra={suspensionTreatment === 'HOLD_RECOGNITION' ? 'Optional — resume manually' : undefined}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}

            {type === 'TERMINATION' && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="refund_amount" label={`Refund due (${ccy})`}>
                    <InputNumber min={0} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item name="write_off_amount" label={`Write-off (${ccy})`}>
                    <InputNumber min={0} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label={t('terminationExpenseBalance')}>
                    <Input readOnly value={amount(terminationExpense, ccy)} status={terminationExpense < 0 ? 'error' : undefined} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="source_document_number" label={t('creditNoteReference')}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="refund_account_id" label={t('refundReceivableAccount')} extra="Blank = company policy default">
                    <Select allowClear showSearch optionFilterProp="label" options={balanceOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="write_off_account_id" label={t('writeOffAccount')} extra="Blank = company policy default">
                    <Select allowClear showSearch optionFilterProp="label" options={expenseOptions} />
                  </Form.Item>
                </Col>
              </>
            )}

            {(type === 'REFUND' || type === 'WRITE_OFF') && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item name="amount" label={`Amount (${ccy})`} rules={[{ required: true }]} extra={`Max ${amount(c.remaining_amount, ccy)}`}>
                    <InputNumber min={0.01} max={Number(c.remaining_amount)} precision={2} stringMode style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item
                    name="account_id"
                    label={type === 'REFUND' ? 'Refund receivable account' : 'Write-off account'}
                    extra="Blank = company policy default"
                  >
                    <Select allowClear showSearch optionFilterProp="label" options={type === 'REFUND' ? balanceOptions : expenseOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item name="source_document_number" label={type === 'REFUND' ? 'Credit note / receipt reference' : 'Reference'}>
                    <Input />
                  </Form.Item>
                </Col>
              </>
            )}

            {type === 'REVERSAL' && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item name="schedule_line_id" label={t('postedLineToReverse')} rules={[{ required: true }]}>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={(postedLines?.data ?? []).map((l) => ({
                        value: l.id,
                        label: `#${l.sequence_no} ${l.accounting_period_key} — ${amount(l.posted_amount, ccy)} — ${l.journal_voucher_number ?? ''}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="re_recognize" valuePropName="checked" label={t('afterReversal')}>
                    <Checkbox disabled={policy ? !policy.allow_re_recognition : false}>{t('scheduleAmountRecognisedAgain')}</Checkbox>
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Card>

        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={submit.isPending} onClick={onSubmit}>
            {needsApproval ? 'Submit for approval' : `Apply ${humanize(type).toLowerCase()}`}
          </Button>
          <Button onClick={() => navigate(contractPath(c.id))}>{tc('cancel')}</Button>
        </Space>
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          <Text type="secondary">Journals post through the General Ledger gateway with the contract&apos;s historical rate; posted lines are never edited.</Text>
        </Paragraph>
      </Form>
    </div>
  );
};

export default PrepaidAdjustmentPage;
